import {
	type DraggableAttributes,
	type DraggableSyntheticListeners,
	useDroppable,
} from "@dnd-kit/core";
import {
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	ArrowDown,
	ChevronDown,
	ChevronUp,
	GripVertical,
	Trash2,
} from "lucide-react";
import type { CSSProperties } from "react";
import { useId, useState } from "react";
import { Badge } from "@/components/base/badges/badges";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { useAdminFormEditorPaths } from "../../../hooks/use-admin-form-editor-paths";
import {
	formBuilderDropSectionFieldStripId,
	formBuilderDropSectionId,
} from "../../../lib/form-builder-drop-target";
import { Plus } from "../../../lib/palette-metadata";
import { getSectionMaxFieldCount } from "../../../lib/section-field-limit";
import type { BuilderNode } from "../../../types";
import { useFormBuilderCanvas } from "../form-builder-canvas-context";
import {
	BUILDER_ROW_ACTIONS_VISIBILITY,
	FORM_BUILDER_ADD_STRIP_BASE,
} from "../form-builder-layout-classes";
import { BUILDER_CONTROL_FOCUS } from "../form-builder-shared-classes";
import { FormBuilderFieldRow } from "./form-builder-field-row";

/**
 * Sections at the root use `useSortable` here only. Root-level fields use
 * `FormBuilderFieldRow`'s sortable hook alone — a second `useSortable` on the parent
 * duplicated the same id and broke drops into nested section targets.
 */
export function FormBuilderRootSectionSortableRow({
	id,
	node,
	selectedId,
	onSelect,
	onMoveSection,
}: {
	id: string;
	node: Extract<BuilderNode, { kind: "section" }>;
	selectedId: string | null;
	onSelect: (id: string) => void;
	onMoveSection: (sectionId: string, dir: "up" | "down") => void;
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<FormBuilderSectionRootRow
			id={id}
			node={node}
			style={style}
			setNodeRef={setNodeRef}
			attributes={attributes}
			listeners={listeners}
			selectedId={selectedId}
			onSelect={onSelect}
			onMoveSection={onMoveSection}
		/>
	);
}

function FormBuilderSectionRootRow({
	id,
	node,
	style,
	setNodeRef,
	attributes,
	listeners,
	selectedId,
	onSelect,
	onMoveSection,
}: {
	id: string;
	node: Extract<BuilderNode, { kind: "section" }>;
	style: CSSProperties;
	setNodeRef: (node: HTMLElement | null) => void;
	attributes: DraggableAttributes;
	listeners: DraggableSyntheticListeners;
	selectedId: string | null;
	onSelect: (id: string) => void;
	onMoveSection: (sectionId: string, dir: "up" | "down") => void;
}) {
	const paths = useAdminFormEditorPaths();
	const { t } = useAppTranslation();
	const { rootIds, clearSectionFields, deleteNode } = useFormBuilderCanvas();
	const { section, childFieldIds } = node;
	const sectionIndex = rootIds.indexOf(id);
	const canMoveSectionUp = sectionIndex > 0;
	const canMoveSectionDown =
		sectionIndex >= 0 && sectionIndex < rootIds.length - 1;
	const sectionHasFields = childFieldIds.length > 0;
	const [open, setOpen] = useState(section.defaultExpanded);
	/** Non-collapsible sections must always show their field list; chevron is schema-driven only. */
	const sectionBodyOpen = !section.collapsible || open;
	const isSelected = selectedId === id;
	const maxFields = getSectionMaxFieldCount(section);
	const isAtFieldCapacity =
		maxFields !== undefined && childFieldIds.length >= maxFields;

	const sectionFieldDropLabelIdle = isAtFieldCapacity
		? t(paths.formEditor.builder.sectionDropZoneFull)
		: t(paths.formEditor.builder.dropFieldInSection);

	const sectionFieldDropLabelDragOver = t(
		paths.formEditor.builder.dropFieldInSectionDragOver,
	);

	const sectionTitleId = useId();
	const sectionPanelId = useId();

	return (
		<li ref={setNodeRef} style={style} className="list-none">
			<section className="relative" aria-labelledby={sectionTitleId}>
				<div
					className={cx(
						"group relative cursor-pointer rounded-lg border p-3 transition-colors",
						isSelected
							? "border-2 border-brand-600 bg-brand-50"
							: "border-border-secondary bg-secondary hover:bg-primary_hover",
					)}
				>
					<div className="flex items-center gap-2">
						<button
							type="button"
							className={cx(
								"cursor-pointer text-fg-quaternary hover:text-fg-secondary",
								BUILDER_CONTROL_FOCUS,
							)}
							aria-label={t(paths.formEditor.a11y.dragHandleSection, {
								name: section.label,
							})}
							{...attributes}
							{...listeners}
						>
							<GripVertical className="size-4" aria-hidden />
						</button>
						{section.collapsible ? (
							<button
								type="button"
								onClick={() => {
									setOpen((v) => !v);
								}}
								className={cx(
									"cursor-pointer rounded p-0 text-text-tertiary hover:bg-background-tertiary",
									BUILDER_CONTROL_FOCUS,
								)}
								aria-expanded={open}
								aria-controls={sectionPanelId}
								aria-label={
									open
										? t(paths.formEditor.a11y.collapseSection, {
												name: section.label,
											})
										: t(paths.formEditor.a11y.expandSection, {
												name: section.label,
											})
								}
							>
								<ChevronDown
									className={cx(
										"size-4 transition-transform",
										open && "rotate-180",
									)}
									aria-hidden
								/>
							</button>
						) : null}
						<button
							type="button"
							className={cx(
								"min-w-0 flex-1 cursor-pointer text-left",
								BUILDER_CONTROL_FOCUS,
							)}
							onClick={() => {
								onSelect(id);
							}}
							aria-pressed={isSelected}
							aria-label={t(paths.formEditor.a11y.editSectionProperties, {
								name: section.label,
							})}
						>
							<div className="flex flex-wrap items-center gap-2">
								<span
									id={sectionTitleId}
									className="font-semibold text-text-primary"
								>
									{section.label}
								</span>
								{section.collapsible ? (
									<Badge color="blue" size="sm" type="pill-color">
										{section.defaultExpanded
											? t(
													paths.formEditor.builder
														.sectionTagCollapsibleExpandedByDefault,
												)
											: t(
													paths.formEditor.builder
														.sectionTagCollapsibleCollapsedByDefault,
												)}
									</Badge>
								) : null}
							</div>
							<div className="text-sm text-text-tertiary">
								{t(paths.formEditor.builder.sectionHeader)}
							</div>
							<div className="mt-0.5 text-xs text-fg-quaternary">
								{maxFields !== undefined
									? t(paths.formEditor.builder.sectionFieldCountCapped, {
											current: childFieldIds.length,
											max: maxFields,
										})
									: t(paths.formEditor.builder.sectionFieldCount, {
											count: childFieldIds.length,
										})}
							</div>
							{section.helpText ? (
								<p className="mt-1 text-xs text-fg-quaternary">
									{section.helpText}
								</p>
							) : null}
						</button>
						<div className={cx("flex gap-1", BUILDER_ROW_ACTIONS_VISIBILITY)}>
							<button
								type="button"
								disabled={!canMoveSectionUp}
								className={cx(
									"rounded p-1 text-text-tertiary",
									BUILDER_CONTROL_FOCUS,
									canMoveSectionUp
										? "cursor-pointer hover:bg-background-tertiary"
										: "cursor-not-allowed opacity-40",
								)}
								onClick={() => {
									if (!canMoveSectionUp) return;
									onMoveSection(id, "up");
								}}
								aria-label={t(paths.formEditor.builder.moveUp)}
							>
								<ChevronUp className="size-4" aria-hidden />
							</button>
							<button
								type="button"
								disabled={!canMoveSectionDown}
								className={cx(
									"rounded p-1 text-text-tertiary",
									BUILDER_CONTROL_FOCUS,
									canMoveSectionDown
										? "cursor-pointer hover:bg-background-tertiary"
										: "cursor-not-allowed opacity-40",
								)}
								onClick={() => {
									if (!canMoveSectionDown) return;
									onMoveSection(id, "down");
								}}
								aria-label={t(paths.formEditor.builder.moveDown)}
							>
								<ArrowDown className="size-4" aria-hidden />
							</button>
							<button
								type="button"
								className={cx(
									"cursor-pointer rounded p-1 text-text-tertiary transition-colors",
									BUILDER_CONTROL_FOCUS,
									"hover:bg-error-50 hover:text-error-600",
								)}
								aria-label={
									sectionHasFields
										? t(paths.formEditor.builder.clearSectionFields)
										: t(paths.formEditor.builder.deleteEmptySection)
								}
								onClick={(e) => {
									e.stopPropagation();
									if (sectionHasFields) {
										clearSectionFields(id);
										return;
									}
									deleteNode(id);
								}}
							>
								<Trash2 className="size-4" aria-hidden />
							</button>
						</div>
					</div>
				</div>

				{/*
				 * When a collapsible section is collapsed, hide the field list and append drop strip until
				 * expanded. Section-level `drop-section::` may have no hit target until then; expand to add
				 * or reorder fields.
				 */}
				<SectionDropZone sectionId={id}>
					{sectionBodyOpen ? (
						<SortableContext
							items={childFieldIds}
							strategy={verticalListSortingStrategy}
						>
							<div
								id={sectionPanelId}
								className="mt-2 flex items-stretch gap-0"
							>
								<div className="relative w-5 shrink-0" aria-hidden>
									<div className="absolute top-0 bottom-0 left-1/2 w-px -translate-x-1/2 bg-border-primary" />
								</div>
								<ul
									className="mb-0 min-w-0 flex-1 list-none space-y-2 pl-4"
									aria-label={t(paths.formEditor.a11y.sectionFieldsList, {
										name: section.label,
									})}
								>
									{childFieldIds.map((fid) => (
										<FormBuilderFieldRow
											key={fid}
											id={fid}
											selectedId={selectedId}
											onSelect={onSelect}
										/>
									))}
									<li className="list-none">
										<SectionFieldDropStrip
											sectionId={id}
											isAtFieldCapacity={isAtFieldCapacity}
											idleLabel={sectionFieldDropLabelIdle}
											dragOverLabel={sectionFieldDropLabelDragOver}
											ariaLabel={t(paths.formEditor.a11y.sectionAppendDrop, {
												name: section.label,
											})}
										/>
									</li>
								</ul>
							</div>
						</SortableContext>
					) : (
						<div id={sectionPanelId} hidden aria-hidden />
					)}
				</SectionDropZone>
			</section>
		</li>
	);
}

function SectionDropZone({
	sectionId,
	children,
}: {
	sectionId: string;
	children: React.ReactNode;
}) {
	const { setNodeRef } = useDroppable({
		id: formBuilderDropSectionId(sectionId),
	});
	return (
		<div ref={setNodeRef} className="min-h-0">
			{children}
		</div>
	);
}

/**
 * Append-field strip with its own droppable id so `isOver` matches drag position (not the whole section).
 */
function SectionFieldDropStrip({
	sectionId,
	isAtFieldCapacity,
	idleLabel,
	dragOverLabel,
	ariaLabel,
}: {
	sectionId: string;
	isAtFieldCapacity: boolean;
	idleLabel: string;
	dragOverLabel: string;
	ariaLabel: string;
}) {
	const { setNodeRef, isOver } = useDroppable({
		id: formBuilderDropSectionFieldStripId(sectionId),
		disabled: isAtFieldCapacity,
	});

	const label = isAtFieldCapacity || !isOver ? idleLabel : dragOverLabel;

	return (
		<section
			ref={setNodeRef}
			aria-label={ariaLabel}
			className={cx(
				isAtFieldCapacity
					? "flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-secondary bg-secondary py-3 text-center text-sm text-fg-quaternary"
					: FORM_BUILDER_ADD_STRIP_BASE,
				!isAtFieldCapacity && isOver && "border-brand-500 bg-brand-50",
			)}
		>
			{isAtFieldCapacity ? null : (
				<Plus className="size-4 shrink-0" aria-hidden />
			)}
			<span>{label}</span>
		</section>
	);
}
